import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { normalizeSlug } from "../utils/pageConfig";
import { createSimpleSection, sectionTypeOptions, structuredSectionConfigs, getStructuredEntries } from "../utils/pageEditorTransforms";
import SectionEditorCard from "./editor/SectionEditorCard";
import BlockTypePreviewMini from "./editor/BlockTypePreviewMini";

const PageEditorForm = ({
    slug, setSlug, navLabel, setNavLabel, title, setTitle,
    footerColumn, setFooterColumn, footerColumnOptions,
    isHome, setIsHome, textOnly, setTextOnly,
    autoSlug, setAutoSlug, optionsExpanded, setOptionsExpanded,
    newSectionType, setNewSectionType,
    sections, setSections, selectedSlug, loading,
    pages, landingId, upsertPageConfig,
    updateSection, removeSection,
    addStructuredLine, editStructuredLine, cancelEdit, removeStructuredLine,
    applySimpleTextFormat, setTextEditorRef,
    onSave, onDelete, editingIndex,
}) => (
    <section className="admin-pages-form">
        <div className="admin-pages-form-columns">
            <div className="admin-pages-form-sidebar">
                <div className="admin-sidebar-group">
                    <h3 className="admin-sidebar-group-title admin-text-sm">
                        <i className="pi pi-info-circle" /> Información de la página
                    </h3>
                    <div className="admin-sidebar-group-content">
                        <div className="admin-pages-row admin-pages-row-page-title">
                            <label className="admin-text-base">Título de la página<span className="admin-sidebar-required">*</span></label>
                            <InputText className="admin-pages-field" value={title} onChange={(event) => {
                                const value = event.target.value;
                                setTitle(value); setNavLabel(value);
                                if (autoSlug) setSlug(normalizeSlug(value));
                            }} placeholder="Ej: Términos y condiciones" />
                            <small className="admin-sidebar-hint admin-text-xs">Este es el título principal que verán los visitantes</small>
                        </div>
                        <div className="admin-pages-row admin-pages-row-page-title">
                            <label>Nombre en el menú</label>
                            <InputText className="admin-pages-field" value={navLabel} onChange={(event) => setNavLabel(event.target.value)} placeholder={title || "Se genera del título"} />
                        </div>
                        <div className="admin-pages-row admin-pages-row-page-title">
                            <div className="admin-pages-section-toolbar-field">
                                <Checkbox inputId="autoSlug" checked={autoSlug} onChange={(e) => { setAutoSlug(e.checked); if (e.checked) setSlug(normalizeSlug(navLabel || title)); }} />
                                <label htmlFor="autoSlug" className="ml-2">URL automática</label>
                                <i className="pi pi-info-circle text-primary text-sm cursor-pointer ml-2" data-pr-tooltip="Si está activa, la URL se genera automáticamente del título. Desactiva para ingresar una URL manual." />
                            </div>
                            <InputText className="admin-pages-field" value={slug} onChange={(e) => setSlug(normalizeSlug(e.target.value))} disabled={autoSlug} placeholder={normalizeSlug(navLabel || title) || "mi-pagina"} />
                            <small className="admin-sidebar-hint admin-text-xs">URL: <strong>/{normalizeSlug(slug) || "..."}</strong></small>
                        </div>
                    </div>
                </div>

                <div className={`admin-sidebar-group admin-sidebar-collapsible ${optionsExpanded ? "expanded" : ""}`}>
                    <h3 className="admin-sidebar-group-title admin-text-sm admin-sidebar-collapsible-trigger" onClick={() => setOptionsExpanded((prev) => !prev)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") setOptionsExpanded((prev) => !prev); }}>
                        <i className="pi pi-cog" /> Configuración avanzada
                        <i className={`pi pi-chevron-${optionsExpanded ? "up" : "down"} admin-sidebar-collapsible-icon`} />
                    </h3>
                    <div className="admin-sidebar-group-content admin-sidebar-collapsible-content">
                        <div className="admin-pages-row admin-pages-row-footer-column">
                            <label className="admin-text-base">Ubicación en el footer</label>
                            <Dropdown className="admin-pages-field" value={footerColumn} options={footerColumnOptions} optionLabel="label" optionValue="value" onChange={(event) => setFooterColumn(Number(event.value) || 0)} />
                            <small className="admin-sidebar-hint admin-text-xs">Indica en qué columna del pie de página aparece el enlace de esta página</small>
                        </div>
                        <div className="admin-pages-row admin-pages-row-footer-column">
                            <label className="admin-text-base">¿Mostrar en la página de inicio?</label>
                            <div className="admin-pages-section-toolbar-field">
                                <span className={!isHome ? "admin-sidebar-toggle-active mr-2" : "mr-2"}>NO</span>
                                <InputSwitch checked={isHome} onChange={(event) => {
                                    const next = event.value; setIsHome(next);
                                    if (next) pages.forEach((p) => { if (p.isHome && p.slug !== selectedSlug) upsertPageConfig(landingId, { ...p, isHome: false }); });
                                }} />
                                <span className={isHome ? "admin-sidebar-toggle-active ml-2" : "ml-2"}>SÍ</span>
                            </div>
                            <small className="admin-sidebar-hint admin-text-xs">{isHome ? "Visible en la página de inicio" : "No visible en la página de inicio"}</small>
                        </div>
                        <div className="admin-pages-row admin-pages-row-footer-column">
                            <label className="admin-text-base">Solo texto (sin navegación)</label>
                            <div className="admin-pages-section-toolbar-field">
                                <span className={!textOnly ? "admin-sidebar-toggle-active mr-2" : "mr-2"}>NO</span>
                                <InputSwitch checked={textOnly} onChange={(event) => setTextOnly(event.value)} />
                                <span className={textOnly ? "admin-sidebar-toggle-active ml-2" : "ml-2"}>SÍ</span>
                            </div>
                            <small className="admin-sidebar-hint admin-text-xs">{textOnly ? "Se muestra como texto en el footer, sin enlace de navegación" : "Navegable como página independiente"}</small>
                        </div>
                    </div>
                </div>

                <div className="admin-sidebar-group">
                    <h3 className="admin-sidebar-group-title admin-text-sm"><i className="pi pi-plus-circle" /> Agregar sección de contenido</h3>
                    <div className="admin-sidebar-group-content">
                        <div className="admin-pages-row">
                            <label className="admin-text-base">Tipo de bloque</label>
                            <Dropdown className="admin-pages-field" value={newSectionType} options={sectionTypeOptions} optionLabel="label" optionValue="value" onChange={(event) => setNewSectionType(event.value)} filter filterPlaceholder="Buscar bloque..." />
                        </div>
                        <div className="admin-sidebar-block-preview"><BlockTypePreviewMini type={newSectionType} /></div>
                        <Button type="button" size="small" icon="pi pi-plus" label="Agregar bloque" onClick={() => setSections((prev) => [...prev, createSimpleSection(newSectionType)])} className="admin-sidebar-add-btn" />
                    </div>
                </div>

                <div className="admin-sidebar-group admin-sidebar-group-actions">
                    <Button type="button" size="small" icon="pi pi-save" label="Guardar página" onClick={onSave} severity="success" disabled={loading} />
                    {selectedSlug && <Button type="button" severity="danger" outlined icon="pi pi-trash" label="Eliminar página" size="small" disabled={loading} onClick={onDelete} />}
                </div>

                <Tooltip target=".pi-info-circle" position="top" />
            </div>

            <div className="admin-pages-form-blocks">
                {sections.map((section, idx) => (
                    <SectionEditorCard
                        key={section._id}
                        section={section}
                        index={idx}
                        structuredConfig={structuredSectionConfigs[section.type]}
                        structuredEntries={structuredSectionConfigs[section.type] ? getStructuredEntries(section.itemsText, structuredSectionConfigs[section.type].fields) : []}
                        updateSection={updateSection}
                        removeSection={removeSection}
                        addStructuredLine={addStructuredLine}
                        editStructuredLine={editStructuredLine}
                        cancelEdit={cancelEdit}
                        removeStructuredLine={removeStructuredLine}
                        editingIndex={editingIndex}
                        applySimpleTextFormat={applySimpleTextFormat}
                        setTextEditorRef={setTextEditorRef}
                    />
                ))}
            </div>
        </div>
    </section>
);

export default PageEditorForm;
